import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Modal, Row } from 'antd';
import type * as MonacoEditor from 'monaco-editor';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { getApiErrorMessage } from '@/services';
import { ReportComponent, SetStateMethod } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import type {
    VantageConfig,
    VantageDataBlock,
    VantageDisplayMode,
    VantageEditorPreferences,
    VantageMetadata,
    VantageQueryHistoryEntry,
    VantageQueryResult,
    VantageValidationResult,
    VantageVisualizationType,
} from '../types';
import { DEFAULT_VANTAGE_EDITOR_PREFERENCES, DEFAULT_VANTAGE_LIMIT, DEFAULT_VANTAGE_SQL } from '../types';
import { createVantageDataBlock, getDataBlockReferenceNameError } from './helpers/datablocks';
import { formatSqlQuery, getClientSqlDiagnostic } from './helpers/query';
import QueryEditor from './layout/01-QueryEditor';
import Settings from './layout/02-Settings';

interface VantageCodeEditorModalProps {
    component: ReportComponent;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const VantageCodeEditorModal: FC<VantageCodeEditorModalProps> = ({ component, open, setOpen }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const initialConfig = useMemo(() => {
        const config = (component.configuration ?? {}) as ReportComponentConfig & VantageConfig;
        const savedSql = config.sql?.trim();

        return {
            sql: config.sql ?? DEFAULT_VANTAGE_SQL,
            hasSavedQuery: Boolean(savedSql),
            visualization: config.visualization ?? 'table',
            displayMode: config.displayMode ?? 'visualization',
            limit: config.limit ?? DEFAULT_VANTAGE_LIMIT,
            preferences: {
                ...DEFAULT_VANTAGE_EDITOR_PREFERENCES,
                ...(config.preferences ?? {}),
            },
            dataBlocks: config.dataBlocks ?? [],
        };
    }, [component.configuration]);

    const [code, setCode] = useState(initialConfig.sql);
    const [visualization, setVisualization] = useState<VantageVisualizationType>(
        initialConfig.visualization as VantageVisualizationType,
    );
    const [displayMode, setDisplayMode] = useState<VantageDisplayMode>(initialConfig.displayMode as VantageDisplayMode);
    const [limit, setLimit] = useState(initialConfig.limit);
    const [preferences, setPreferences] = useState<VantageEditorPreferences>(initialConfig.preferences);
    const [dataBlocks, setDataBlocks] = useState<VantageDataBlock[]>(initialConfig.dataBlocks);
    const [metadata, setMetadata] = useState<VantageMetadata>({ tables: [], variables: [] });
    const [metadataError, setMetadataError] = useState<string | null>(null);
    const [validation, setValidation] = useState<VantageValidationResult | null>(null);
    const [result, setResult] = useState<VantageQueryResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [runtimeMs, setRuntimeMs] = useState<number | null>(null);
    const [queryHistory, setQueryHistory] = useState<VantageQueryHistoryEntry[]>([]);
    const editorRef = useRef<MonacoEditor.editor.IStandaloneCodeEditor | null>(null);

    const insertAtCursor = useCallback(
        (value: string) => {
            const editor = editorRef.current;

            if (!editor) {
                setCode((currentCode) => `${currentCode}${currentCode ? ' ' : ''}${value}`);
                return;
            }

            const selection = editor.getSelection();

            if (!selection) {
                return;
            }

            editor.executeEdits('vantage-variable', [{ range: selection, text: value, forceMoveMarkers: true }]);
            editor.focus();
        },
        [setCode],
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        setCode(initialConfig.sql);
        setVisualization(initialConfig.visualization as VantageVisualizationType);
        setDisplayMode(initialConfig.displayMode as VantageDisplayMode);
        setLimit(initialConfig.limit);
        setPreferences(initialConfig.preferences);
        setDataBlocks(initialConfig.dataBlocks);
        setValidation(null);
        setResult(null);
        setError(null);
        setRuntimeMs(null);
        // Initialize from persisted configuration only when this editor session opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component.id, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        authorizedAxiosInstance
            .get('/api/vantage-queries/metadata')
            .then((res) => {
                if (cancelled) {
                    return;
                }

                setMetadata({
                    tables: res.data?.tables ?? [],
                    variables: res.data?.variables ?? [],
                });
                setMetadataError(null);
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }

                setMetadata({ tables: [], variables: [] });
                setMetadataError(getApiErrorMessage(err, 'Unable to load Vantage metadata.'));
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    const validateCode = useCallback(async (sql: string): Promise<VantageValidationResult> => {
        const trimmedCode = sql.trim();
        const clientDiagnostic = getClientSqlDiagnostic(trimmedCode);

        if (!trimmedCode) {
            return { executable: false, message: 'Query cannot be empty.' };
        }

        if (clientDiagnostic) {
            return { executable: false, message: clientDiagnostic };
        }

        try {
            const res = await authorizedAxiosInstance.post('/api/vantage-queries/validate', { sql: trimmedCode });

            return res.data;
        } catch (err: unknown) {
            return {
                executable: false,
                message: getApiErrorMessage(err, 'Unable to validate Vantage query.'),
            };
        }
    }, []);

    useEffect(() => {
        if (!open || !preferences.validateOnChange) {
            return;
        }

        let cancelled = false;
        const trimmedCode = code.trim();
        const clientDiagnostic = getClientSqlDiagnostic(trimmedCode);

        if (!trimmedCode) {
            setValidation({ executable: false, message: 'Query cannot be empty.' });
            return;
        }

        if (clientDiagnostic) {
            setValidation({ executable: false, message: clientDiagnostic });
            return;
        }

        setValidating(true);

        const timeout = window.setTimeout(() => {
            validateCode(trimmedCode)
                .then((nextValidation) => {
                    if (!cancelled) {
                        setValidation(nextValidation);
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setValidating(false);
                    }
                });
        }, 450);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
            setValidating(false);
        };
    }, [code, open, preferences.validateOnChange, validateCode]);

    const prepareSqlForExecution = (sql = code, formatOnRun = preferences.formatSqlOnRun) => {
        const formattedCode = formatOnRun ? formatSqlQuery(sql) : sql;

        if (formattedCode !== code) {
            setCode(formattedCode);
        }

        return formattedCode;
    };

    const formatQuery = () => {
        setCode(formatSqlQuery(code));
        editorRef.current?.focus();
    };

    const canRunOrSave = preferences.validateOnChange
        ? validation?.executable === true && !validating
        : Boolean(code.trim()) && !getClientSqlDiagnostic(code.trim());

    const metadataWithDataBlocks = useMemo<VantageMetadata>(() => {
        const variablesByKey = new Map(metadata.variables.map((variable) => [variable.key, variable]));

        dataBlocks.forEach((dataBlock) => {
            variablesByKey.set(dataBlock.referenceName, {
                key: dataBlock.referenceName,
                label: dataBlock.referenceName,
                type: 'array' as const,
            });
        });

        return {
            ...metadata,
            variables: [...variablesByKey.values()],
        };
    }, [dataBlocks, metadata]);
    const reservedMetadataVariables = useMemo(() => {
        const localReferenceNames = new Set(dataBlocks.map((dataBlock) => dataBlock.referenceName));

        return metadata.variables.filter((variable) => !localReferenceNames.has(variable.key));
    }, [dataBlocks, metadata.variables]);

    const persistVantageConfiguration = async (
        executableCode: string,
        nextDataBlocks: VantageDataBlock[],
        executionOptions: {
            displayMode: VantageDisplayMode;
            limit: number;
            visualization: VantageVisualizationType;
        },
    ) => {
        await authorizedAxiosInstance.patch(`/api/report-components/${component.id}/configuration`, {
            configuration: {
                ...(component.configuration ?? {}),
                type: component.type,
                sql: executableCode,
                visualization: executionOptions.visualization,
                displayMode: executionOptions.displayMode,
                limit: executionOptions.limit,
                preferences,
                dataBlocks: nextDataBlocks,
            },
        });
    };

    const updateDataBlockReferenceName = async (dataBlockId: string, referenceName: string) => {
        const previousDataBlock = dataBlocks.find((dataBlock) => dataBlock.id === dataBlockId);
        const nextDataBlocks = dataBlocks.map((dataBlock) =>
            dataBlock.id === dataBlockId
                ? {
                      ...dataBlock,
                      referenceName,
                      updatedAt: new Date().toISOString(),
                  }
                : dataBlock,
        );
        const targetDataBlock = nextDataBlocks.find((dataBlock) => dataBlock.id === dataBlockId);
        const errorMessage = targetDataBlock
            ? getDataBlockReferenceNameError(
                  targetDataBlock.referenceName,
                  reservedMetadataVariables,
                  nextDataBlocks,
                  dataBlockId,
              )
            : 'Unknown datablock.';

        if (errorMessage) {
            return;
        }

        setDataBlocks(nextDataBlocks);

        try {
            await persistVantageConfiguration(code, nextDataBlocks, {
                displayMode,
                limit,
                visualization,
            });
        } catch (err: unknown) {
            if (previousDataBlock) {
                setDataBlocks((currentDataBlocks) =>
                    currentDataBlocks.map((dataBlock) =>
                        dataBlock.id === dataBlockId && dataBlock.referenceName === referenceName
                            ? previousDataBlock
                            : dataBlock,
                    ),
                );
            }

            toast.error('Unable to Rename Datablock', {
                description: getApiErrorMessage(err, 'The original Datablock name has been restored.'),
            });
            throw err;
        }
    };

    const deleteDataBlock = async (dataBlockId: string) => {
        const deletedDataBlock = dataBlocks.find((dataBlock) => dataBlock.id === dataBlockId);
        const deletedDataBlockIndex = dataBlocks.findIndex((dataBlock) => dataBlock.id === dataBlockId);
        const nextDataBlocks = dataBlocks.filter((dataBlock) => dataBlock.id !== dataBlockId);

        setDataBlocks(nextDataBlocks);

        try {
            await persistVantageConfiguration(code, nextDataBlocks, {
                displayMode,
                limit,
                visualization,
            });
        } catch (err: unknown) {
            if (deletedDataBlock) {
                setDataBlocks((currentDataBlocks) => {
                    if (currentDataBlocks.some((dataBlock) => dataBlock.id === dataBlockId)) {
                        return currentDataBlocks;
                    }

                    const restoredDataBlocks = [...currentDataBlocks];
                    restoredDataBlocks.splice(
                        Math.min(Math.max(deletedDataBlockIndex, 0), restoredDataBlocks.length),
                        0,
                        deletedDataBlock,
                    );
                    return restoredDataBlocks;
                });
            }

            toast.error('Unable to Delete Datablock', {
                description: getApiErrorMessage(err, 'The Datablock has been restored.'),
            });
            throw err;
        }
    };

    const getReferenceNameError = (dataBlockId: string, referenceName: string) =>
        getDataBlockReferenceNameError(referenceName, reservedMetadataVariables, dataBlocks, dataBlockId);

    const executeQuery = async (
        sql = code,
        executionOptions: {
            displayMode?: VantageDisplayMode;
            formatSqlOnRun?: boolean;
            limit?: number;
            persistDataBlock?: boolean;
            trackHistory?: boolean;
            visualization?: VantageVisualizationType;
        } = {},
    ): Promise<VantageQueryResult | null> => {
        const executableCode = prepareSqlForExecution(sql, executionOptions.formatSqlOnRun);
        const executionLimit = executionOptions.limit ?? limit;
        const executionVisualization = executionOptions.visualization ?? visualization;
        const executionDisplayMode = executionOptions.displayMode ?? displayMode;
        const shouldPersistDataBlock = executionOptions.persistDataBlock ?? true;
        const shouldTrackHistory = executionOptions.trackHistory ?? true;
        const nextValidation = await validateCode(executableCode);

        setValidation(nextValidation);

        if (!nextValidation.executable) {
            setError(nextValidation.message ?? 'Query must be valid executable SQL before it can run.');
            return null;
        }

        setRunning(true);
        setError(null);

        try {
            const res = await authorizedAxiosInstance.post('/api/vantage-queries/execute', {
                sql: executableCode,
                limit: executionLimit,
            });
            const nextResult = res.data as VantageQueryResult;

            setResult(nextResult);
            setRuntimeMs(nextResult.runtimeMs);

            let nextDataBlock: VantageDataBlock | undefined;

            if (shouldPersistDataBlock) {
                nextDataBlock = createVantageDataBlock({
                    dataBlocks,
                    displayMode: executionDisplayMode,
                    limit: executionLimit,
                    result: nextResult,
                    sql: executableCode,
                    variables: reservedMetadataVariables,
                    visualization: executionVisualization,
                });
                const nextDataBlocks = [nextDataBlock, ...dataBlocks].slice(0, 25);

                setDataBlocks(nextDataBlocks);

                await persistVantageConfiguration(executableCode, nextDataBlocks, {
                    displayMode: executionDisplayMode,
                    limit: executionLimit,
                    visualization: executionVisualization,
                });
            }

            if (shouldTrackHistory) {
                setQueryHistory((previousHistory) =>
                    [
                        {
                            id: `${Date.now()}`,
                            dataBlockId: nextDataBlock?.id,
                            referenceName: nextDataBlock?.referenceName,
                            sql: executableCode,
                            visualization: executionVisualization,
                            displayMode: executionDisplayMode,
                            limit: executionLimit,
                            rowCount: nextResult.rowCount,
                            runtimeMs: nextResult.runtimeMs,
                            ranAt: new Date().toISOString(),
                        },
                        ...previousHistory,
                    ].slice(0, 12),
                );
            }

            return nextResult;
        } catch (err: unknown) {
            setError(getApiErrorMessage(err, 'Unable to execute Vantage query.'));
            return null;
        } finally {
            setRunning(false);
        }
    };

    const runQuery = () => {
        executeQuery(code);
    };

    useEffect(() => {
        if (!open || !initialConfig.hasSavedQuery) {
            return;
        }

        executeQuery(initialConfig.sql, {
            displayMode: initialConfig.displayMode as VantageDisplayMode,
            formatSqlOnRun: initialConfig.preferences.formatSqlOnRun,
            limit: initialConfig.limit,
            persistDataBlock: false,
            trackHistory: false,
            visualization: initialConfig.visualization as VantageVisualizationType,
        });
        // Run only when a saved modal configuration is opened/refreshed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component.id, open]);

    const saveConfiguration = async () => {
        const executableCode = prepareSqlForExecution();
        const nextValidation = await validateCode(executableCode);

        setValidation(nextValidation);

        if (!nextValidation.executable) {
            setError(nextValidation.message ?? 'Query must be valid executable SQL before it can be saved.');
            return;
        }

        setSaving(true);

        try {
            await executeQuery(executableCode, {
                displayMode,
                formatSqlOnRun: false,
                limit,
                persistDataBlock: true,
                trackHistory: true,
                visualization,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            closable={false}
            mask={true}
            title={null}
            footer={null}
            style={{
                minWidth: 'calc(100vw - 25rem)',
            }}
            styles={{
                body: {
                    minHeight: 'calc(100vh - 15rem)',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    overflow: 'hidden',
                },
                content: { padding: 0 },
                // mask: {
                //     backgroundColor: token.colorBgMask,
                //     backdropFilter: 'blur(0.25rem)',
                //     WebkitBackdropFilter: 'blur(0.25rem)',
                // },
            }}
            centered
            onCancel={() => setOpen(false)}
        >
            <Row style={{ height: 'calc(100vh - 15rem)' }}>
                <Col span={12} style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                    <QueryEditor
                        editorRef={editorRef}
                        code={code}
                        setCode={setCode}
                        visualization={visualization}
                        preferences={preferences}
                        metadata={metadataWithDataBlocks}
                        metadataError={metadataError}
                        validation={validation}
                        validating={validating}
                        result={result}
                        error={error}
                        runtimeMs={runtimeMs}
                        running={running}
                        saving={saving}
                        canRunOrSave={canRunOrSave}
                        runQuery={runQuery}
                        saveConfiguration={saveConfiguration}
                        formatQuery={formatQuery}
                    />
                </Col>
                <Col
                    span={12}
                    style={{
                        borderLeft: `1px solid ${token.colorBorder}`,
                        height: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Settings
                        setOpen={setOpen}
                        code={code}
                        setCode={setCode}
                        visualization={visualization}
                        setVisualization={setVisualization}
                        displayMode={displayMode}
                        setDisplayMode={setDisplayMode}
                        limit={limit}
                        setLimit={setLimit}
                        preferences={preferences}
                        setPreferences={setPreferences}
                        metadata={metadataWithDataBlocks}
                        metadataError={metadataError}
                        validation={validation}
                        result={result}
                        runtimeMs={runtimeMs}
                        error={error}
                        runQuery={runQuery}
                        insertAtCursor={insertAtCursor}
                        dataBlocks={dataBlocks}
                        deleteDataBlock={deleteDataBlock}
                        updateDataBlockReferenceName={updateDataBlockReferenceName}
                        getDataBlockReferenceNameError={getReferenceNameError}
                        queryHistory={queryHistory}
                    />
                </Col>
            </Row>
        </Modal>
    );
};

export default VantageCodeEditorModal;
